import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  AlertTriangle, 
  FileText, 
  Users,
  Building2,
  Shield,
  BarChart3,
  Check,
  Briefcase,
  TrendingUp,
  Globe,
  Lock
} from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-wrapper">
      {/* Navbar */}
      <nav className="landing-navbar">
        <div className="landing-navbar-container">
          <div className="landing-navbar-logo">
            <img 
              src="https://wavcfonfn7v2m.ok.kimi.link/images/black-handshake.png" 
              alt="DealNexa" 
              className="landing-navbar-logo-img"
            />
            <span>DealNexa</span>
          </div>
          <div className="landing-navbar-links">
            <button onClick={() => scrollToSection('problem')}>Product</button>
            <button onClick={() => scrollToSection('modules')}>Modules</button>
            <button onClick={() => scrollToSection('usecases')}>Use Cases</button>
            <button onClick={() => scrollToSection('why')}>About</button>
          </div>
          <div className="landing-navbar-actions">
            <button className="landing-nav-signin" onClick={() => navigate('/login')}>
              Sign in
            </button>
            <button className="landing-nav-cta" onClick={() => navigate('/signup')}>
              Get started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-badge">
          <span className="landing-badge-icon">⚡</span>
          <span>Built for M&amp;A deal teams</span>
        </div>

        <h1 className="landing-hero-title">
          More diligence. Less time.<br />Better deals.
        </h1>

        <p className="landing-hero-subtitle">
          Time is the enemy of deals. DealNexa gives your team the infrastructure to run
          deeper, faster due diligence — so you can answer every IC question, reduce deal
          fatigue, and close with confidence inside the exclusivity window.
        </p>

        {/* Product Preview */}
        <div className="landing-hero-preview">
          <div className="landing-preview-window">
            <div className="landing-preview-header">
              <div className="landing-preview-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="landing-preview-title">DealNexa — Pipeline</span>
            </div>
            <div className="landing-preview-content">
              <div className="landing-preview-card">
                <div className="landing-preview-dot blue"></div>
                <div className="landing-preview-info">
                  <h4>Acme Corp</h4>
                  <span className="landing-preview-tag">3 flags</span>
                  <p>Due Diligence</p>
                  <strong>$48M</strong>
                </div>
              </div>
              <div className="landing-preview-card">
                <div className="landing-preview-dot green"></div>
                <div className="landing-preview-info">
                  <h4>Orion Capital</h4>
                  <p>Letter of Intent</p>
                  <strong>$120M</strong>
                </div>
              </div>
              <div className="landing-preview-card">
                <div className="landing-preview-dot gray"></div>
                <div className="landing-preview-info">
                  <h4>Nexus Group</h4>
                  <p>Initial Review</p>
                  <strong>$22M</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="landing-section landing-problem">
        <div className="landing-section-container">
          <div className="landing-section-badge">THE PROBLEM</div>
          <h2 className="landing-section-title">"Time is the enemy of deals" — and your tools are losing the race</h2>
          <p className="landing-section-subtitle">
            Once an LOI is signed, the clock starts. Every day inside the exclusivity window
            that your team spends chasing files, formatting reports, or waiting on answers is
            a day closer to deal fatigue — and a blown close.
          </p>

          <div className="landing-problem-grid">
            <div className="landing-problem-card">
              <div className="landing-card-icon">
                <Search size={20} />
              </div>
              <h3>Diligence gaps that kill IC meetings</h3>
              <p>"We didn't have time" is not an answer in an Investment Committee. Unanswered questions send teams back to the data room and cost weeks.</p>
            </div>
            <div className="landing-problem-card">
              <div className="landing-card-icon">
                <AlertTriangle size={20} />
              </div>
              <h3>Deal fatigue on both sides</h3>
              <p>Sellers are exhausted by long diligence cycles. Buyers who appear disorganized or slow lose seller goodwill — and sometimes the deal itself.</p>
            </div>
            <div className="landing-problem-card">
              <div className="landing-card-icon">
                <FileText size={20} />
              </div>
              <h3>Documents buried in shared drives</h3>
              <p>Critical contracts, financials, and legal docs scattered across email threads and Dropbox folders. The answer is in there somewhere — if you can find it.</p>
            </div>
            <div className="landing-problem-card">
              <div className="landing-card-icon">
                <Users size={20} />
              </div>
              <h3>Advisors and counsel working blind</h3>
              <p>Lawyers, bankers, and analysts each have a piece of the picture. Without a shared workspace, duplication and miscommunication are inevitable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Modules Section */}
      <section id="modules" className="landing-section landing-modules">
        <div className="landing-section-container">
          <div className="landing-section-badge">PLATFORM MODULES</div>
          <h2 className="landing-section-title">Do more diligence in the same window</h2>
          <p className="landing-section-subtitle">
            Every hour saved on logistics, document hunting, and status updates is an hour
            your team can spend on deeper analysis. DealNexa is built to maximize what you
            accomplish inside your exclusivity period.
          </p>

          <div className="landing-modules-grid">
            <div className="landing-module-card">
              <div className="landing-module-header">
                <div className="landing-module-icon blue">
                  <Building2 size={18} />
                </div>
                <span className="landing-module-label">PIPELINE + CRM</span>
              </div>
              <h3>Full deal lifecycle management</h3>
              <p>Track active deals by stage on a live Kanban board. Import your existing pipeline from Excel, Salesforce, or HubSpot — DealNexa maps your columns automatically.</p>
              <ul className="landing-module-features">
                <li><Check size={14} /> LOI-to-close stage tracking</li>
                <li><Check size={14} /> Import from any CRM or spreadsheet</li>
                <li><Check size={14} /> Full historical deal record</li>
              </ul>
            </div>

            <div className="landing-module-card">
              <div className="landing-module-header">
                <div className="landing-module-icon purple">
                  <Shield size={18} />
                </div>
                <span className="landing-module-label">DILIGENCE</span>
              </div>
              <h3>Answer every IC question before it's asked</h3>
              <p>Upload financials, contracts, and legal docs. DealNexa reads them instantly — extracting key figures, flagging risks, and surfacing the issues that matter before they surface in a board meeting.</p>
              <ul className="landing-module-features">
                <li><Check size={14} /> AI document analysis</li>
                <li><Check size={14} /> Risk flag detection</li>
                <li><Check size={14} /> Diligence checklist tracker</li>
              </ul>
            </div>

            <div className="landing-module-card">
              <div className="landing-module-header">
                <div className="landing-module-icon orange">
                  <BarChart3 size={18} />
                </div>
                <span className="landing-module-label">REPORTING</span>
              </div>
              <h3>IC-ready reports without the prep work</h3>
              <p>Stop spending the night before an IC meeting formatting slides. DealNexa generates deal summaries, pipeline health snapshots, and diligence status reports in seconds.</p>
              <ul className="landing-module-features">
                <li><Check size={14} /> Deal summary generation</li>
                <li><Check size={14} /> Pipeline status reports</li>
                <li><Check size={14} /> Export-ready output</li>
              </ul>
            </div>

            <div className="landing-module-card">
              <div className="landing-module-header">
                <div className="landing-module-icon green">
                  <Users size={18} />
                </div>
                <span className="landing-module-label">COLLABORATION</span>
              </div>
              <h3>One deal room for your entire team</h3>
              <p>Bring bankers, lawyers, and analysts into a shared workspace with role-based access. No more version confusion. No more "which file is current?" Every action is logged.</p>
              <ul className="landing-module-features">
                <li><Check size={14} /> Role-based access control</li>
                <li><Check size={14} /> Complete audit trail</li>
                <li><Check size={14} /> Advisor &amp; counsel access</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="usecases" className="landing-section landing-usecases">
        <div className="landing-section-container">
          <div className="landing-section-badge">USE CASES</div>
          <h2 className="landing-section-title">Built for professionals who live inside the exclusivity window</h2>
          <p className="landing-section-subtitle">
            M&amp;A deals don't fail because buyers didn't want them. They fail because teams
            ran out of time, lost momentum, or couldn't answer the hard questions fast enough.
            DealNexa is built for the people under that pressure.
          </p>

          <div className="landing-usecases-grid">
            <div className="landing-usecase-card">
              <div className="landing-card-icon">
                <Briefcase size={20} />
              </div>
              <span className="landing-usecase-label">PRIVATE EQUITY</span>
              <h3>Close more deals in the same 60-day window</h3>
              <p>Run deeper diligence on more targets in parallel without adding headcount. Know exactly where every deal stands — and what's still open — before your IC meeting.</p>
            </div>

            <div className="landing-usecase-card">
              <div className="landing-card-icon">
                <TrendingUp size={20} />
              </div>
              <span className="landing-usecase-label">M&amp;A ADVISORY</span>
              <h3>Show sellers you're organized. Close faster.</h3>
              <p>Sellers and their advisors judge buyers by how they run diligence. Show up prepared, move quickly, and reduce the seller exhaustion that kills deals late in the process.</p>
            </div>

            <div className="landing-usecase-card">
              <div className="landing-card-icon">
                <Globe size={20} />
              </div>
              <span className="landing-usecase-label">CORPORATE DEVELOPMENT</span>
              <h3>Answer the Board before they ask</h3>
              <p>Walk into every IC and Board meeting with comprehensive diligence. No more "we didn't have time to look at that" — the answer that sends you back to the data room for another two weeks.</p>
            </div>

            <div className="landing-usecase-card">
              <div className="landing-card-icon">
                <Lock size={20} />
              </div>
              <span className="landing-usecase-label">LEGAL &amp; COMPLIANCE</span>
              <h3>One thread from NDA to closing table</h3>
              <p>Every document, annotation, and decision captured in one place. A complete chain of custody from first NDA to final signature — built for reps, warranties, and post-close disputes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why DealNexa Section */}
      <section id="why" className="landing-section landing-why">
        <div className="landing-section-container">
          <div className="landing-why-grid">
            <div className="landing-why-content">
              <div className="landing-section-badge">WHY DEALNEXA</div>
              <h2 className="landing-section-title">The platform that gets you to "enough diligence" — faster</h2>
              <p className="landing-why-description">
                No buyer walks into an IC and says "we didn't have time." DealNexa gives your
                deal team the infrastructure to do comprehensive diligence at speed — so when
                the exclusivity clock runs out, you've answered every material question.
              </p>
              <ul className="landing-why-features">
                <li><Check size={16} /> AI reads your documents and flags the issues before your IC meeting does</li>
                <li><Check size={16} /> Migrate from Excel, Salesforce, or HubSpot — any column structure, any format</li>
                <li><Check size={16} /> One deal room for your team, advisors, and counsel — with full audit trail</li>
                <li><Check size={16} /> Track every deal from first contact through post-close integration</li>
                <li><Check size={16} /> Built for PE, corp dev, and advisory firms that live inside the 60-day window</li>
              </ul>
              <button 
                className="landing-why-cta"
                onClick={() => navigate('/signup')}
              >
                Start for free
              </button>
            </div>

            <div className="landing-why-stats">
              <div className="landing-stat-card">
                <span className="landing-stat-value">60 days</span>
                <span className="landing-stat-label">Typical exclusivity window. We help you make every one count.</span>
              </div>
              <div className="landing-stat-card highlight">
                <span className="landing-stat-value">100%</span>
                <span className="landing-stat-label">Diligence coverage — every document, question, and action logged</span>
              </div>
              <div className="landing-stat-card">
                <span className="landing-stat-value">Any CRM</span>
                <span className="landing-stat-label">Import from Excel, Salesforce, HubSpot, or any spreadsheet format</span>
              </div>
              <div className="landing-stat-card">
                <span className="landing-stat-value">Zero gaps</span>
                <span className="landing-stat-label">Walk into every IC with every material question already answered</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer-section">
        <div className="landing-footer-container">
          <div className="landing-footer-brand">
            <div className="landing-logo-icon">DN</div>
            <span>DealNexa</span>
          </div>
          <p className="landing-footer-copy">© 2026 DealNexa. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
